'use client'
import { type FC, memo, useMemo} from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import type { TypeCmsProductDetail, TypeLang } from '@/schema'
import { createUnderline, getP12, mq, vw } from '@/styles'

import { Link } from './commons'
import { LOCALE } from '@/utils'


type Props = {
  data: {
    category: TypeCmsProductDetail['category']
    filter: TypeCmsProductDetail['filter']
    name: TypeCmsProductDetail['name']
  }
}


const BREADCRUMBS_TRANSLATION = {
  en: 'Breadcrumbs',
  es: 'Ruta de navegación',
  ca: 'Ruta de navegació',
  fr: 'Fil d\'Ariane.',
  pt: 'Trilha de navegação',
  de: 'Navigationspfad',
  it: 'Percorso di navigazione',
} as const

export const Breadcrumbs:FC<Props> = memo(({ data }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const { category, filter, name } = data
  const paramKey = useMemo(() => Object.keys(filter?.url?.params ?? {})[0], [filter])
  const paramValue = useMemo(() => filter?.url?.params?.[paramKey]?.value, [filter?.url?.params, paramKey])
  const filterHref = useMemo(() => filter?.url?.slug ? `/${filter?.url?.page}/${filter?.url?.slug}?${paramKey}=${paramValue}` : undefined, [filter, paramKey, paramValue])

  return (
    <Div aria-label={BREADCRUMBS_TRANSLATION[lang ?? LOCALE]}>
      <Link href={`/${category?.url?.page}/${category?.url?.page}`}>
        {category?.name}
      </Link>,{' '}
      {filterHref &&
        <Link href={filterHref}>
          {filter?.name}
        </Link>
      }, <span aria-current='page'>{name}</span>
    </Div>
  )
})

const Div = styled.div`
  ${getP12()}
  grid-column: 1 / span 6;
  margin-top: ${vw(20, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    margin-top: ${vw(20, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 12;
    margin-top: ${vw(20, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin-top: 20px;
  }

  a {
    ${getP12()}
    ${createUnderline({ reverse: true })};
  }
`
